import { memo, useCallback } from 'react';

import { type TrackCopiedShareLinkParams, useTrackCopiedShareLink } from '@air/analytics';
import { CheckIcon, CopyIcon, PencilIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';

export type ShowcaseActionConfirmationToastContentProps = {
  message: string;
  shareUrl: string;
  onDismiss: () => void;
  /** When provided, copying the link from this toast fires the `Copied a share url` event. */
  copyTracking?: TrackCopiedShareLinkParams;
  /** Navigates to the link's edit surface; the caller owns the destination. */
  onEdit: () => void;
};

export const ShowcaseActionConfirmationToastContent = memo(
  ({ message, shareUrl, onDismiss, copyTracking, onEdit }: ShowcaseActionConfirmationToastContentProps) => {
    const { copyTextToClipboard } = useCopyTextToClipboard();
    const { trackCopiedShareLink } = useTrackCopiedShareLink();

    const onClickHandleEdit = useCallback(() => {
      onEdit();
      onDismiss();
    }, [onEdit, onDismiss]);

    const onClickHandleCopyLink = useCallback(() => {
      // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
      copyTextToClipboard({ text: shareUrl, successMessage: 'Link copied' });
      if (copyTracking) {
        trackCopiedShareLink(copyTracking);
      }
      onDismiss();
    }, [shareUrl, onDismiss, copyTextToClipboard, trackCopiedShareLink, copyTracking]);

    return (
      <div className="flex items-center justify-between gap-4 overflow-hidden rounded-12 ads:bg-base bg-gray-1 p-4 shadow-popover">
        <div className="flex items-center gap-4">
          <div className="shrink-0 rounded-full ads:bg-primary-subtle bg-blue-2 p-2">
            <CheckIcon size="lg" className="ads:text-icon-primary text-blue-9" />
          </div>
          <strong className="ads:text-primary text-14 text-gray-12">{message}</strong>
        </div>
        <div className="flex items-center gap-2">
          <Button
            appearance="outline"
            color="gray"
            size="small"
            data-testid="SHARE_LINK_TOAST_COPY_BUTTON"
            onClick={onClickHandleCopyLink}
            prefix={<CopyIcon size="xs" />}
            type="button"
            className="shrink-0"
          >
            Copy link
          </Button>
          <Button
            appearance="outline"
            color="gray"
            size="small"
            data-testid="SHARE_LINK_TOAST_EDIT_BUTTON"
            onClick={onClickHandleEdit}
            prefix={<PencilIcon size="xs" />}
            type="button"
            className="shrink-0"
          >
            Edit
          </Button>
        </div>
      </div>
    );
  },
);

ShowcaseActionConfirmationToastContent.displayName = 'ShowcaseActionConfirmationToastContent';
